import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ClientSelect from 'components/form/ClientSelect';
import CustomSelect from 'components/form/CustomSelect';
import DateTimePicker from 'components/form/DateTimePicker';
import FinancialYearSelect from 'components/form/FinancialYearSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import MemberSelect from 'components/form/MemberSelect';
import PartnerSelect from 'components/form/PartnerSelect';
import ProjectSelect from 'components/form/ProjectSelect';
import TicketSelect from 'components/form/TicketSelect';
import UserSelect from 'components/form/UserSelect';
import { ClientResult } from 'stores/ClientStoreAdapter';
import { FinancialYearResult } from 'stores/FinancialYearStoreAdapter';
import { MemberListResult } from 'stores/MemberStoreAdapter';
import { PartnerResult } from 'stores/PartnerStoreAdapter';
import { ProjectListResult } from 'stores/ProjectStoreAdapter';
import {
  SearchConditionResult,
  createSearchCondition,
  deleteSearchCondition,
  listSearchCondition,
  setDefaultSearchCondition,
  unsetDefaultSearchCondition,
} from 'stores/SearchConditionStoreAdapter';
import { TicketListResult } from 'stores/TicketStoreAdapter';
import { listUserProfile, saveUserProfile } from 'stores/UserProfileStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { generateUUID } from 'utils/Common';
import { parseProfileValue } from 'utils/UserProfileGuard';

export type Condition = {
  key: string;
  name: string;
  type:
    | 'text'
    | 'katakana-text'
    | 'select'
    | 'select-multi'
    | 'radio'
    | 'check'
    | 'user-select-single'
    | 'user-select-multi'
    | 'client-select-single'
    | 'client-select-multi'
    | 'member-select-single'
    | 'partner-select-single'
    | 'project-select-single'
    | 'ticket-select-single'
    | 'manager-select-single'
    | 'financial-year-select'
    | 'date-time-picker-start'
    | 'date-time-picker-end';
  color: string;
  value?: Value;
  values?: Value[];
  /** user-select-single / user-select-multi で使用するユーザーリスト */
  users?: UserResult[];
  /** client-select-single / client-select-multi で使用する取引先リスト */
  clients?: ClientResult[];
  /** member-select-single で使用する要員リスト */
  members?: MemberListResult[];
  /** partner-select-single で使用するパートナーリスト */
  partners?: PartnerResult[];
  /** project-select-single で使用するプロジェクトリスト */
  projects?: ProjectListResult[];
  /** ticket-select-single で使用するチケット候補リスト（親チケット選択等） */
  tickets?: TicketListResult[];
  /** manager-select-single で使用する課長リスト（UserResult[]として扱う） */
  managers?: UserResult[];
  /** @deprecated financialYears は FinancialYearSelect 内部で取得するため不要 */
  financialYears?: FinancialYearResult[];
};

export type Order = {
  key: string;
  name: string;
};

export type Value = {
  key: string;
  name: string;
};

export type Filter = {
  id: string;
  key: string;
  color: string;
  value: Value;
};

export type SearchFilterProps = {
  filters: Filter[];
  conditions: Condition[];
  orders: Order[];
  show?: boolean;
  onFiltersChange?: (filters: Filter[]) => void;
  /** 検索条件保存機能用の条件キー（画面ごとに固定値を設定） */
  conditionKey?: string;
  /**
   * user_profile(AUTO) 用の profile_key（画面ごとに一意・例: "member-partner-list-search"）。
   * 指定時、初期表示で「デフォルト★の次段」として最後の検索条件を復元し、
   * 検索実行（onFiltersChange）のたびに最後の検索条件を保存する。
   * デフォルト★が設定済みの場合・クエリパラメータがある場合は復元しない（優先度に従う）。
   */
  userProfileKey?: string;
  /** 条件詳細ダイアログを外部から開く場合にtrueを設定 */
  detailDialogOpen?: boolean;
  /** 条件詳細ダイアログのclose通知 */
  onDetailDialogClose?: () => void;
};

const SearchFilter: React.FC<SearchFilterProps> = ({
  filters: initialFilters,
  conditions,
  orders,
  show = true,
  onFiltersChange,
  conditionKey,
  userProfileKey,
  detailDialogOpen = false,
  onDetailDialogClose,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [filters, setFilters] = useState<Filter[]>(initialFilters);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [savedConditions, setSavedConditions] = useState<SearchConditionResult[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  // URLクエリパラメータの有無（初期表示の優先度判定に使用）
  const hasQueryParams = searchParams.toString() !== '';
  // conditionKey ごとにデフォルト適用の初回判定を1度だけ行うためのフラグ
  const defaultAppliedRef = useRef(false);

  // initialFiltersが変更された場合に同期する
  useEffect(() => {
    if (initialFilters.length > 0) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  // 保存済み検索条件を取得
  const fetchSavedConditions = useCallback(() => {
    if (!conditionKey) return Promise.resolve<SearchConditionResult[]>([]);
    return listSearchCondition(conditionKey)
      .then((results) => {
        setSavedConditions(results);
        return results;
      })
      .catch(() => [] as SearchConditionResult[]);
  }, [conditionKey]);

  // conditionKey が変わったらデフォルト適用フラグをリセット
  useEffect(() => {
    defaultAppliedRef.current = false;
  }, [conditionKey]);

  // user_profile AUTO の復元が完了したか（保存が復元前の初期値で走らないようにするガード）
  const profileRestoredRef = useRef(false);

  // conditionKey / userProfileKey が変わったら復元完了フラグをリセット
  useEffect(() => {
    profileRestoredRef.current = false;
  }, [userProfileKey]);

  // 初期表示: 保存済み条件を取得し、優先度に従って初期検索条件を適用する。
  // 優先度: クエリパラメータ > search_condition のデフォルト★ > user_profile の AUTO > 画面固有デフォルト
  useEffect(() => {
    fetchSavedConditions().then((results) => {
      if (defaultAppliedRef.current) return;
      defaultAppliedRef.current = true;
      // (1) クエリパラメータがある場合は以降を参照しない（最優先）
      if (hasQueryParams) {
        profileRestoredRef.current = true;
        return;
      }
      // (2) search_condition のデフォルト★を探して適用・自動検索する
      const defaultCondition = results.find((c) => c.isDefault);
      if (defaultCondition) {
        try {
          const restoredFilters: Filter[] = JSON.parse(defaultCondition.conditionValue);
          setFilters(restoredFilters);
          onFiltersChange?.(restoredFilters);
        } catch {
          // JSON パースに失敗した場合は無視（画面固有のデフォルトにフォールバック）
        }
        // デフォルト★が効いた画面では user_profile 復元は行わない
        profileRestoredRef.current = true;
        return;
      }
      // (3) user_profile の AUTO（最後の検索条件）を復元・自動検索する（profileKey 指定時のみ）
      if (!userProfileKey) {
        profileRestoredRef.current = true;
        return;
      }
      listUserProfile(userProfileKey, 'AUTO')
        .then((profiles) => {
          if (profiles.length > 0) {
            const restored = parseProfileValue<Filter[] | null>(profiles[0].profileValue, null);
            if (Array.isArray(restored) && restored.length > 0) {
              setFilters(restored);
              onFiltersChange?.(restored);
            }
          }
        })
        .catch(() => {
          // 復元失敗は画面固有デフォルトにフォールバック（画面を壊さない）
        })
        .finally(() => {
          profileRestoredRef.current = true;
        });
      // (4) user_profile も無ければ画面固有デフォルト（v1挙動を変えない）
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSavedConditions]);

  // 外部から条件詳細ダイアログを開く
  useEffect(() => {
    if (detailDialogOpen) {
      setMenuOpen(true);
    }
  }, [detailDialogOpen]);

  // フィルター変更を親へ通知しつつ、user_profile(AUTO) に最後の検索条件を保存する。
  // 検索は明示操作で頻度が低いためデバウンス不要。保存失敗は握りつぶす（UX を止めない）。
  // 復元完了前・profileKey 未指定・クエリパラメータ優先時は保存しない。
  const emitFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange?.(newFilters);
    if (userProfileKey && profileRestoredRef.current && !hasQueryParams) {
      saveUserProfile({
        profileKey: userProfileKey,
        profileValue: JSON.stringify(newFilters),
      }).catch(() => {
        // 保存失敗は無視
      });
    }
  };

  if (!show) return null;

  const handleAdd = (filter: Filter) => {
    // 同一keyのフィルターが既にある場合は置き換える
    const newFilters = [...filters.filter((e) => e.key !== filter.key), filter];
    setFilters(newFilters);
    setMenuOpen(false);
    onDetailDialogClose?.();
    emitFiltersChange(newFilters);
  };

  const handleDelete = (filter: Filter) => () => {
    const newFilters = filters.filter((e) => e.id !== filter.id);
    setFilters(newFilters);
    emitFiltersChange(newFilters);
  };

  const handleSearchAreaClick = () => {
    if (conditionKey) {
      setDropdownOpen(true);
    } else {
      setMenuOpen(true);
    }
  };

  const handleSaveCondition = () => {
    if (!conditionKey || filters.length === 0) return;
    const now = new Date();
    const conditionName = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const conditionValue = JSON.stringify(filters);
    createSearchCondition({ conditionKey, conditionName, conditionValue })
      .then(() => {
        fetchSavedConditions();
      })
      .catch(() => {});
  };

  const handleDeleteCondition = (searchConditionId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteSearchCondition({ searchConditionId })
      .then(() => {
        fetchSavedConditions();
      })
      .catch(() => {});
  };

  // ★/☆ トグル: デフォルト検索条件の設定/解除
  const handleToggleDefault = (condition: SearchConditionResult) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const prevConditions = savedConditions;
    if (condition.isDefault) {
      // ★ → ☆: 解除。楽観更新後、失敗時はロールバック
      setSavedConditions((prev) =>
        prev.map((c) =>
          c.searchConditionId === condition.searchConditionId ? { ...c, isDefault: false } : c,
        ),
      );
      unsetDefaultSearchCondition({ searchConditionId: condition.searchConditionId }).catch(() => {
        setSavedConditions(prevConditions);
      });
    } else {
      // ☆ → ★: 設定。同一 condition_key の他条件は全て解除（バックエンドの付け替えと一致させる）
      setSavedConditions((prev) =>
        prev.map((c) => ({
          ...c,
          isDefault: c.searchConditionId === condition.searchConditionId,
        })),
      );
      setDefaultSearchCondition({ searchConditionId: condition.searchConditionId }).catch(() => {
        setSavedConditions(prevConditions);
      });
    }
  };

  const handleRestoreCondition = (condition: SearchConditionResult) => {
    try {
      const restoredFilters: Filter[] = JSON.parse(condition.conditionValue);
      setFilters(restoredFilters);
      setDropdownOpen(false);
      emitFiltersChange(restoredFilters);
    } catch {
      // JSON パースに失敗した場合は無視
    }
  };

  return (
    <React.Fragment>
      <Paper
        ref={anchorRef}
        elevation={0}
        onClick={handleSearchAreaClick}
        sx={{
          border: 1,
          borderColor: menuOpen || dropdownOpen ? 'primary.main' : 'transparent',
          borderRadius: 2,
          bgcolor: 'action.hover',
          transition: 'border-color 0.2s',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          minHeight: 40,
          px: 1.5,
          py: 0.5,
          gap: 1,
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {filters.map((e) => (
            <Chip
              size="small"
              key={e.id}
              label={e.value.name}
              onDelete={handleDelete(e)}
              sx={{
                bgcolor: e.color,
                color: '#fff',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* 保存済み検索条件プルダウン */}
      {conditionKey && (
        <Popper
          open={dropdownOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth || 'auto' }}
        >
          <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Paper
              elevation={8}
              sx={{
                borderRadius: 1,
                mt: 0.5,
                overflow: 'hidden',
                maxHeight: 280,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* 保存済み条件一覧 */}
              <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                {savedConditions.length === 0 ? (
                  <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      保存済みの検索条件はありません
                    </Typography>
                  </Box>
                ) : (
                  savedConditions.map((sc) => {
                    let chipFilters: Filter[] = [];
                    try {
                      chipFilters = JSON.parse(sc.conditionValue);
                    } catch {
                      // ignore
                    }
                    return (
                      <Box
                        key={sc.searchConditionId}
                        onClick={() => handleRestoreCondition(sc)}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          transition: 'background-color 0.15s',
                          bgcolor: sc.isDefault ? 'action.selected' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={handleToggleDefault(sc)}
                          sx={{ flexShrink: 0, p: 0.25 }}
                        >
                          {sc.isDefault ? (
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                        </IconButton>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            {sc.conditionName}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              flexWrap: 'nowrap',
                              overflow: 'hidden',
                            }}
                          >
                            {chipFilters.map((f, idx) => (
                              <Chip
                                key={idx}
                                size="small"
                                label={f.value.name}
                                sx={{
                                  bgcolor: f.color,
                                  color: '#fff',
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={handleDeleteCondition(sc.searchConditionId)}
                          sx={{ flexShrink: 0, p: 0.25 }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </IconButton>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* 保存ボタン（固定） */}
              <Divider />
              <Box sx={{ px: 1.5, py: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  fullWidth
                  startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                  disabled={filters.length === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveCondition();
                  }}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    justifyContent: 'flex-start',
                    px: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  現在の検索条件を保存
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}

      <FilterMenu
        open={menuOpen}
        handleClose={() => {
          setMenuOpen(false);
          onDetailDialogClose?.();
        }}
        addFilter={(e: Filter) => handleAdd(e)}
        conditions={conditions}
      />
    </React.Fragment>
  );
};

function FilterMenu({
  open,
  handleClose,
  addFilter,
  conditions,
}: {
  open: boolean;
  handleClose: () => void;
  addFilter: (filter: Filter) => void;
  conditions: Condition[];
}) {
  const [condition, setCondition] = useState(conditions[0]);

  const [textValue, setTextValue] = useState('');
  const [katakanaTextValue, setKatakanaTextValue] = useState('');
  const [selectValue, setSelectValue] = useState<Value | null>(
    condition.values ? condition.values[0] : null,
  );
  const [selectMultiValue, setSelectMultiValue] = useState<string[]>([]);
  const [radioValue, setRadioValue] = useState<Value | null>(
    condition.values ? condition.values[0] : null,
  );
  const [checkValue, setCheckValue] = useState(false);
  const [userSingleValue, setUserSingleValue] = useState<string | null>(null);
  const [userMultiValue, setUserMultiValue] = useState<string[]>([]);
  const [clientSingleValue, setClientSingleValue] = useState<string | null>(null);
  const [clientMultiValue, setClientMultiValue] = useState<string[]>([]);
  const [memberSingleValue, setMemberSingleValue] = useState<string | null>(null);
  const [partnerSingleValue, setPartnerSingleValue] = useState<string | null>(null);
  const [projectSingleValue, setProjectSingleValue] = useState<string | null>(null);
  const [ticketSingleValue, setTicketSingleValue] = useState<string | null>(null);
  const [managerSingleValue, setManagerSingleValue] = useState<string | null>(null);
  const [financialYearValue, setFinancialYearValue] = useState<string>('');
  const [dateValue, setDateValue] = useState('');

  const handleSelectValueChange = (key: string) => {
    setSelectValue(condition.values!.find((e) => e.key === key)!);
  };
  const handleRadioValueChange = (key: string) => {
    setRadioValue(condition.values!.find((e) => e.key === key)!);
  };

  const handleChange = (key: string) => {
    const c = conditions.find((e) => e.key === key)!;
    setTextValue('');
    setKatakanaTextValue('');
    setCheckValue(false);
    setSelectMultiValue([]);
    setUserSingleValue(null);
    setUserMultiValue([]);
    setClientSingleValue(null);
    setClientMultiValue([]);
    setMemberSingleValue(null);
    setPartnerSingleValue(null);
    setProjectSingleValue(null);
    setTicketSingleValue(null);
    setManagerSingleValue(null);
    setFinancialYearValue('');
    setDateValue('');
    if (c.type === 'select' || c.type === 'radio') {
      setSelectValue(c.values![0]);
      setRadioValue(c.values![0]);
    } else {
      setSelectValue(null);
      setRadioValue(null);
    }
    setCondition(c);
  };

  const getUserName = (userId: string): string => {
    const user = condition.users?.find((u) => u.userId === userId);
    return user ? user.userName : userId;
  };

  const getClientName = (clientId: string): string => {
    const client = condition.clients?.find((c) => c.clientId === clientId);
    return client ? client.clientName : clientId;
  };

  const getMemberName = (memberId: string): string => {
    const member = condition.members?.find((m) => m.memberId === memberId);
    return member ? member.memberName : memberId;
  };

  const getPartnerName = (partnerId: string): string => {
    const partner = condition.partners?.find((p) => p.partnerId === partnerId);
    return partner ? partner.partnerName : partnerId;
  };

  // チケット選択のチップ表示は管理IDにする（画面には親チケットの管理IDが出るように）
  const getTicketLabel = (ticketId: string): string => {
    const ticket = condition.tickets?.find((t) => t.ticketId === ticketId);
    return ticket ? ticket.managementId : ticketId;
  };

  const getProjectName = (projectId: string): string => {
    const project = condition.projects?.find((p) => p.projectId === projectId);
    return project ? project.projectName : projectId;
  };

  const getManagerName = (managerId: string): string => {
    const manager = condition.managers?.find((u) => u.userId === managerId);
    return manager ? manager.userName : managerId;
  };

  const handleAdd = () => {
    if (condition.type === 'text' && textValue !== '') {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: textValue, name: `${condition.name}: ${textValue}` },
      });
      setTextValue('');
      return;
    }
    if (condition.type === 'katakana-text' && katakanaTextValue !== '') {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: katakanaTextValue, name: `${condition.name}: ${katakanaTextValue}` },
      });
      setKatakanaTextValue('');
      return;
    }
    if (condition.type === 'select' && selectValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: selectValue.key, name: `${condition.name}: ${selectValue.name}` },
      });
      return;
    }
    if (condition.type === 'select-multi' && selectMultiValue.length > 0) {
      // 複数選択値をカンマ区切りで1つのフィルターとして追加（user/client multi と同じ流儀）
      const names = selectMultiValue
        .map((k) => condition.values?.find((v) => v.key === k)?.name ?? k)
        .join(', ');
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: selectMultiValue.join(','), name: `${condition.name}: ${names}` },
      });
      setSelectMultiValue([]);
      return;
    }
    if (condition.type === 'radio' && radioValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: radioValue.key, name: `${condition.name}: ${radioValue.name}` },
      });
      return;
    }
    if (condition.type === 'check' && checkValue && condition.value) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: condition.value.key, name: condition.name },
      });
      return;
    }
    if (condition.type === 'user-select-single' && userSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: userSingleValue,
          name: `${condition.name}: ${getUserName(userSingleValue)}`,
        },
      });
      setUserSingleValue(null);
      return;
    }
    if (condition.type === 'user-select-multi' && userMultiValue.length > 0) {
      // 複数ユーザーをカンマ区切りで1つのフィルターとして追加
      const names = userMultiValue.map((id) => getUserName(id)).join(', ');
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: userMultiValue.join(','), name: `${condition.name}: ${names}` },
      });
      setUserMultiValue([]);
      return;
    }
    if (condition.type === 'client-select-single' && clientSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: clientSingleValue,
          name: `${condition.name}: ${getClientName(clientSingleValue)}`,
        },
      });
      setClientSingleValue(null);
      return;
    }
    if (condition.type === 'client-select-multi' && clientMultiValue.length > 0) {
      const names = clientMultiValue.map((id) => getClientName(id)).join(', ');
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: clientMultiValue.join(','), name: `${condition.name}: ${names}` },
      });
      setClientMultiValue([]);
      return;
    }
    if (condition.type === 'member-select-single' && memberSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: memberSingleValue,
          name: `${condition.name}: ${getMemberName(memberSingleValue)}`,
        },
      });
      setMemberSingleValue(null);
      return;
    }
    if (condition.type === 'partner-select-single' && partnerSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: partnerSingleValue,
          name: `${condition.name}: ${getPartnerName(partnerSingleValue)}`,
        },
      });
      setPartnerSingleValue(null);
      return;
    }
    if (condition.type === 'project-select-single' && projectSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: projectSingleValue,
          name: `${condition.name}: ${getProjectName(projectSingleValue)}`,
        },
      });
      setProjectSingleValue(null);
      return;
    }
    if (condition.type === 'ticket-select-single' && ticketSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: ticketSingleValue,
          name: `${condition.name}: ${getTicketLabel(ticketSingleValue)}`,
        },
      });
      setTicketSingleValue(null);
      return;
    }
    if (condition.type === 'manager-select-single' && managerSingleValue) {
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: {
          key: managerSingleValue,
          name: `${condition.name}: ${getManagerName(managerSingleValue)}`,
        },
      });
      setManagerSingleValue(null);
      return;
    }
    if (condition.type === 'financial-year-select' && financialYearValue !== '') {
      const fy = condition.financialYears?.find(
        (f) => String(f.financialYear) === financialYearValue,
      );
      const displayName = fy ? `${fy.financialYear}期` : financialYearValue;
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: financialYearValue, name: `${condition.name}: ${displayName}` },
      });
      setFinancialYearValue('');
      return;
    }
    if (
      (condition.type === 'date-time-picker-start' || condition.type === 'date-time-picker-end') &&
      dateValue !== ''
    ) {
      // 表示用は日付部分のみ (yyyy-MM-dd)
      const displayDate = dateValue.substring(0, 10);
      addFilter({
        id: generateUUID(),
        key: condition.key,
        color: condition.color,
        value: { key: dateValue, name: `${condition.name}: ${displayDate}` },
      });
      setDateValue('');
      return;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          検索条件
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Stack gap={2}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="nowrap">
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={condition.key}
                onChange={(e) => handleChange(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {conditions.map((e: Condition) => (
                  <MenuItem key={e.key} value={e.key}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleAdd()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              追加
            </Button>
          </Stack>
          <Box sx={{ width: '100%' }}>
            {condition.type === 'text' ? (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder={condition.name}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            ) : condition.type === 'katakana-text' ? (
              <KatakanaTextField
                fullWidth
                size="small"
                placeholder={condition.name}
                value={katakanaTextValue}
                onChange={setKatakanaTextValue}
                onKeyDown={handleKeyDown}
              />
            ) : condition.type === 'select' ? (
              <Select
                fullWidth
                size="small"
                value={selectValue ? selectValue.key : ''}
                onChange={(e) => handleSelectValueChange(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {condition.values!.map((e: Value) => (
                  <MenuItem key={e.key} value={e.key}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            ) : condition.type === 'select-multi' ? (
              <CustomSelect
                mode="multi"
                label={condition.name}
                options={(condition.values ?? []).map((v) => ({ value: v.key, label: v.name }))}
                value={selectMultiValue}
                onChange={setSelectMultiValue}
              />
            ) : condition.type === 'radio' ? (
              <RadioGroup
                value={radioValue ? radioValue.key : ''}
                onChange={(e) => handleRadioValueChange(e.target.value)}
              >
                {condition.values!.map((e: Value) => (
                  <FormControlLabel
                    key={e.key}
                    value={e.key}
                    control={<Radio size="small" />}
                    label={e.name}
                  />
                ))}
              </RadioGroup>
            ) : condition.type === 'user-select-single' ? (
              <UserSelect
                mode="single"
                users={condition.users || []}
                selectedUserId={userSingleValue}
                onChange={setUserSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'user-select-multi' ? (
              <UserSelect
                mode="multi"
                users={condition.users || []}
                selectedUserIds={userMultiValue}
                onChange={setUserMultiValue}
                label={condition.name}
              />
            ) : condition.type === 'client-select-single' ? (
              <ClientSelect
                mode="single"
                clients={condition.clients || []}
                selectedClientId={clientSingleValue}
                onChange={setClientSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'client-select-multi' ? (
              <ClientSelect
                mode="multi"
                clients={condition.clients || []}
                selectedClientIds={clientMultiValue}
                onChange={setClientMultiValue}
                label={condition.name}
              />
            ) : condition.type === 'member-select-single' ? (
              <MemberSelect
                mode="single"
                members={condition.members || []}
                selectedMemberId={memberSingleValue}
                onChange={setMemberSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'partner-select-single' ? (
              <PartnerSelect
                mode="single"
                partners={condition.partners || []}
                selectedPartnerId={partnerSingleValue}
                onChange={setPartnerSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'project-select-single' ? (
              <ProjectSelect
                mode="single"
                projects={condition.projects || []}
                selectedProjectId={projectSingleValue}
                onChange={setProjectSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'ticket-select-single' ? (
              <TicketSelect
                tickets={condition.tickets || []}
                selectedTicketId={ticketSingleValue}
                onChange={(t) => setTicketSingleValue(t?.ticketId ?? null)}
                label={condition.name}
              />
            ) : condition.type === 'manager-select-single' ? (
              <UserSelect
                mode="single"
                users={condition.managers || []}
                selectedUserId={managerSingleValue}
                onChange={setManagerSingleValue}
                label={condition.name}
              />
            ) : condition.type === 'financial-year-select' ? (
              <FinancialYearSelect
                value={financialYearValue}
                onChange={setFinancialYearValue}
                label={condition.name}
              />
            ) : condition.type === 'date-time-picker-start' ? (
              <DateTimePicker
                mode="start"
                value={dateValue}
                onChange={setDateValue}
                fullWidth
                size="small"
                label={condition.name}
              />
            ) : condition.type === 'date-time-picker-end' ? (
              <DateTimePicker
                mode="end"
                value={dateValue}
                onChange={setDateValue}
                fullWidth
                size="small"
                label={condition.name}
              />
            ) : (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkValue}
                      onChange={(e) => setCheckValue(e.target.checked)}
                    />
                  }
                  label={condition.value!.name}
                />
              </FormGroup>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default SearchFilter;
